import React, { useState } from 'react';
import { Card, Row, Col, Badge, Button, Carousel } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBed,
  faBath,
  faRuler,
  faMapMarkerAlt,
  faHeart,
  faEye,
  faShare,
  faBuilding,
  faTrophy,
  faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import { faHeart as faHeartRegular } from '@fortawesome/free-regular-svg-icons';
import './CompactPropertyCard.css';

interface CompactPropertyCardProps {
  property: any;
  onView?: () => void;
  onSave?: () => void;
  onSendToClient?: () => void;
  isSaved?: boolean;
  matchScore?: number;
  showDescription?: boolean;
  className?: string;
}

const CompactPropertyCard: React.FC<CompactPropertyCardProps> = ({
  property,
  onView,
  onSave,
  onSendToClient,
  isSaved = false,
  matchScore,
  showDescription = false,
  className = ''
}) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const images = property.images?.length > 0 ? property.images : 
                 property.image ? [property.image] : 
                 ['/placeholder-home.jpg'];
  
  const handleImageSelect = (selectedIndex: number, e: any) => {
    if (e?.stopPropagation) e.stopPropagation();
    setActiveImageIndex(selectedIndex);
  };
  
  return (
    <Card className={`compact-property-card shadow-sm ${className}`}>
      <style>{`
        .compact-property-card .carousel,
        .compact-property-card .carousel-inner,
        .compact-property-card .carousel-item,
        .compact-property-card .carousel-item.active {
          height: 100% !important;
        }
      `}</style>
      <Row className="g-0">
        {/* Property Image Carousel */}
        <Col md={4} style={{ position: 'relative', overflow: 'hidden', minHeight: '200px' }}>
          <div className="property-image-wrapper" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
            {images.length > 1 ? (
              <Carousel
                activeIndex={activeImageIndex}
                onSelect={handleImageSelect}
                interval={null}
                indicators={false}
                controls={images.length > 1}
                className="property-carousel"
                style={{ height: '100%' }}
              >
                {images.map((image: string, index: number) => (
                  <Carousel.Item key={index} style={{ height: '100%' }}>
                    <img
                      src={image}
                      alt={`${property.address} - Photo ${index + 1}`}
                      className="property-image d-block w-100"
                      onClick={onView}
                      style={{ cursor: 'pointer', width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0 }}
                    />
                  </Carousel.Item>
                ))}
              </Carousel>
            ) : (
              <img
                src={images[0]}
                alt={property.address}
                className="property-image d-block"
                onClick={onView}
                style={{ cursor: 'pointer', width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', top: 0, left: 0 }}
              />
            )}
            {images.length > 1 && (
              <div className="position-absolute bottom-0 end-0 m-2 bg-dark text-white px-2 py-1 rounded" style={{ opacity: 0.85, fontSize: '0.75rem' }}>
                {activeImageIndex + 1}/{images.length}
              </div>
            )}

            {matchScore && matchScore >= 90 && (
              <Badge bg="success" className="match-badge position-absolute top-0 start-0 m-2">
                <FontAwesomeIcon icon={faTrophy} className="me-1" />
                Perfect Match
              </Badge>
            )}

            {images.length > 1 && (
              <div className="image-count position-absolute bottom-0 end-0 m-2 bg-dark text-white px-2 py-1 rounded" style={{ opacity: 0.85 }}>
                <i className="fa fa-camera"></i> {activeImageIndex + 1}/{images.length}
              </div>
            )}
          </div>
        </Col>

        {/* Property Details */}
        <Col md={8}>
          <Card.Body className="p-3">
            <Row>
              <Col lg={8}>
                {/* Price and Address */}
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <h5 className="property-price mb-1">
                      ${property.price?.toLocaleString() || property.list_price?.toLocaleString()}
                    </h5>
                    <p className="property-address mb-1">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1 text-muted" />
                      {property.address || property.street_address}
                    </p>
                    {(property.city || property.zip || property.postal_code) && (
                      <p className="property-city text-muted mb-0">
                        {[property.city, property.state, property.zip || property.postal_code].filter(Boolean).join(', ')}
                      </p>
                    )}
                    {(property.listingBrokerage || property.listing_office_name || property.listingAgent || property.listing_agent_full_name) && (
                      <>
                        <p className="text-muted mb-0" style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                          <FontAwesomeIcon icon={faBuilding} className="me-1" />
                          Listing courtesy of {property.listingBrokerage || property.listing_office_name || (typeof property.listingAgent === 'object' ? property.listingAgent?.officeName : '') || 'MLS Partner'}
                          {(property.listing_agent_full_name || (typeof property.listingAgent === 'string' ? property.listingAgent : property.listingAgent?.name)) && ` - ${property.listing_agent_full_name || (typeof property.listingAgent === 'string' ? property.listingAgent : property.listingAgent?.name)}`}
                        </p>
                        <p className="text-muted mb-0" style={{ fontSize: '0.65rem', fontStyle: 'italic' }}>
                          This property is not representative of the displaying agent's or brokerage's transaction history.
                        </p>
                      </>
                    )}
                  </div>
                  <div className="text-end">
                    {property.mls_number && (
                      <Badge bg="primary" className="mb-2">
                        MLS# {property.mls_number}
                      </Badge>
                    )}
                    {matchScore && (
                      <div>
                        <Badge 
                          bg={matchScore >= 80 ? 'success' : matchScore >= 60 ? 'primary' : 'warning'}
                          className="match-score-badge"
                        >
                          {matchScore}% Match
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>

                {/* Property Stats */}
                <div className="property-stats mb-2">
                  <span className="stat-item">
                    <FontAwesomeIcon icon={faBed} className="me-1" />
                    {property.bedrooms || property.beds || 0} beds
                  </span>
                  <span className="stat-item">
                    <FontAwesomeIcon icon={faBath} className="me-1" />
                    {property.bathrooms || property.baths || 0} baths
                  </span>
                  <span className="stat-item">
                    <FontAwesomeIcon icon={faRuler} className="me-1" />
                    {(property.squareFeet || property.square_feet || property.living_area || property.sqft || 0).toLocaleString()} sqft
                  </span>
                </div>

                {/* MLS and Listing Info */}
                <div className="listing-info mb-2">
                  <div className="border rounded p-2 bg-light">
                    <div className="d-flex align-items-center mb-1">
                      <FontAwesomeIcon icon={faBuilding} className="me-2 text-primary" />
                      <strong className="text-dark small">
                        {property.listingAgent?.officeName || property.listingBrokerage || 'MLS Partner'}
                      </strong>
                    </div>
                    {property.listingAgent?.name && (
                      <div className="text-muted small ms-3">
                        Listed by {property.listingAgent.name}
                      </div>
                    )}
                  </div>
                  <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                    <i className="fa fa-info-circle me-1"></i>
                    Data from Arizona Regional Multiple Listing Service, Inc.
                  </div>
                </div>

                {/* Property Description (truncated) */}
                {showDescription && property.description && (
                  <p className="property-description mb-2 text-muted small">
                    {property.description.length > 150 
                      ? property.description.substring(0, 150) + '...'
                      : property.description
                    }
                  </p>
                )}
              </Col>

              {/* Action Buttons */}
              <Col lg={4} className="text-end">
                <div className="action-buttons d-flex flex-column gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={onView}
                    className="w-100"
                  >
                    <FontAwesomeIcon icon={faEye} className="me-1" />
                    View Details
                  </Button>
                  
                  <div className="d-flex gap-2">
                    <Button
                      variant={isSaved ? "success" : "outline-secondary"}
                      size="sm"
                      onClick={onSave}
                      className="flex-fill"
                    >
                      <FontAwesomeIcon icon={isSaved ? faHeart : faHeartRegular} />
                    </Button>
                    
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={onSendToClient}
                      className="flex-fill"
                      title="Send to client"
                    >
                      <FontAwesomeIcon icon={faEnvelope} />
                    </Button>
                    
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="flex-fill"
                      onClick={() => {
                        if (navigator.share) {
                          navigator.share({
                            title: property.address,
                            text: `Check out this property: ${property.bedrooms} bed, ${property.bathrooms} bath`,
                            url: window.location.href
                          });
                        }
                      }}
                    >
                      <FontAwesomeIcon icon={faShare} />
                    </Button>
                  </div>
                </div>

                {/* Additional Property Info */}
                {property.year_built && (
                  <div className="mt-2 text-muted small text-end">
                    Built {property.year_built}
                  </div>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Col>
      </Row>
    </Card>
  );
};

export default CompactPropertyCard;